import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaSearch, FaChevronLeft, FaChevronRight, FaHistory, FaEye } from "react-icons/fa";
import Modal from "../Modal";

const API = "/api/bitacora";
const LIMITE = 15;

const MODULOS = [
  { value: "salida", label: "Salidas de dinero" },
  { value: "entrada", label: "Entradas (diezmos y ofrendas)" },
  { value: "miembro", label: "Miembros" },
  { value: "reunion", label: "Reuniones" },
  { value: "usuario", label: "Usuarios y permisos" },
  { value: "campamento", label: "Campamentos" },
  { value: "abono", label: "Abonos de campamentos" },
  { value: "gasto_campamento", label: "Gastos de campamentos" },
];

const ACCIONES = [
  { value: "crear", label: "Creó", estilo: "bg-emerald-50 text-emerald-700" },
  { value: "editar", label: "Modificó", estilo: "bg-indigo-50 text-indigo-700" },
  { value: "desactivar", label: "Desactivó", estilo: "bg-rose-50 text-rose-700" },
  { value: "reactivar", label: "Reactivó", estilo: "bg-sky-50 text-sky-700" },
  { value: "permisos", label: "Cambió permisos", estilo: "bg-amber-50 text-amber-700" },
  { value: "adjuntar", label: "Adjuntó archivos", estilo: "bg-slate-100 text-slate-700" },
  { value: "quitar_adjunto", label: "Quitó un archivo", estilo: "bg-slate-100 text-slate-700" },
];

// Nombres legibles de los campos que se guardan en el historial.
const CAMPOS = {
  fecha: "Fecha", concepto: "Concepto", cantidad: "Cantidad", tipo: "Tipo", id_categoria: "Categoría (id)",
  diezmo: "Diezmo", misiones: "Misiones", protemplo: "Pro-templo", ofrenda: "Ofrenda", biblia: "Biblia", especial: "Especial",
  total: "Total", id_servicio: "Servicio (id)", id_miembro: "Miembro (id)",
  nombre: "Nombre", paterno: "Apellido paterno", materno: "Apellido materno", direccion: "Dirección", telefonos: "Teléfonos",
  email: "Correo", edocivil: "Estado civil", nacimiento: "Nacimiento", aniversario: "Aniversario",
  empresa_trabajo: "Empresa", telefono_trabajo: "Teléfono del trabajo", escuela: "Escuela", telefono_escuela: "Teléfono de la escuela",
  status: "Activo", sexo: "Sexo", estatus: "Activo", ap_paterno: "Apellido paterno", ap_materno: "Apellido materno", telefono: "Teléfono",
  asunto: "Asunto", resultado: "Resultado", llevaron: "Quién llevó la reunión", votaron: "Votaron", personas: "Persona(s)",
  tema: "Tema", fecha_inicio: "Fecha de inicio", fecha_fin: "Fecha de término", tarifas: "Tarifas", tarifa: "Tarifa", beca: "Beca",
  iglesia: "Iglesia", asistente: "Asistente", monto: "Monto", forma_pago: "Forma de pago", saldo: "Saldo pendiente",
  fecha_bautismo: "Fecha de bautismo", fecha_ingreso: "Fecha de ingreso", contrasena: "Contraseña", modulo: "Módulo",
};

const nombreCampo = (campo) => CAMPOS[campo] || campo;
const nombreModulo = (valor) => MODULOS.find((m) => m.value === valor)?.label || valor;

const formatoValor = (valor) => {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (typeof valor === "boolean") return valor ? "Sí" : "No";
  if (Array.isArray(valor)) return valor.length ? valor.join(", ") : "—";
  return String(valor);
};

// "2026-10-16 14:05:09" (hora de México, ya viene así de la API)
const formatoFechaHora = (valor) => {
  if (!valor) return "";
  const [dia, hora] = valor.split(" ");
  const [a, m, d] = dia.split("-");
  return `${d}/${m}/${a} ${hora.slice(0, 5)}`;
};

export default function HistorialAdmin() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [modulo, setModulo] = useState("");
  const [accion, setAccion] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [detalle, setDetalle] = useState(null);

  const totalPages = Math.ceil(total / LIMITE);

  useEffect(() => {
    const t = setTimeout(() => { setBusqueda(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, limit: LIMITE, search: busqueda, modulo, accion, desde, hasta });
      const res = await fetch(`${API}?${params.toString()}`, { credentials: "include" });
      if (!res.ok) throw new Error("Error obteniendo el historial");
      const result = await res.json();
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      console.error(err);
      setError("No se pudo cargar el historial de cambios");
    } finally {
      setLoading(false);
    }
  }, [page, busqueda, modulo, accion, desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const inicioRango = total === 0 ? 0 : (page - 1) * LIMITE + 1;
  const finRango = Math.min(page * LIMITE, total);
  const clasesFiltro = "border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400";

  return (
    <div className="p-6">

      {/* BREADCRUMB */}
      <div className="text-sm text-slate-400 mb-3">
        <Link to="/dashboard/administracion" className="hover:text-indigo-600">Administración</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-600 font-medium">Historial de cambios</span>
      </div>

      {/* HEADER */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Historial de cambios</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Quién creó, modificó o desactivó información en Tesorería, Miembros, Reuniones y Usuarios. Solo lectura.
        </p>
      </div>

      {/* FILTROS */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div className="relative flex-1 min-w-[220px]">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-sm" />
          <input
            placeholder="Buscar por usuario o descripción..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Módulo</label>
          <select value={modulo} onChange={(e) => { setModulo(e.target.value); setPage(1); }} className={clasesFiltro}>
            <option value="">Todos</option>
            {MODULOS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Acción</label>
          <select value={accion} onChange={(e) => { setAccion(e.target.value); setPage(1); }} className={clasesFiltro}>
            <option value="">Todas</option>
            {ACCIONES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Desde</label>
          <input type="date" value={desde} onChange={(e) => { setDesde(e.target.value); setPage(1); }} className={clasesFiltro} />
        </div>

        <div>
          <label className="text-xs text-slate-500 mb-1 block">Hasta</label>
          <input type="date" value={hasta} onChange={(e) => { setHasta(e.target.value); setPage(1); }} className={clasesFiltro} />
        </div>
      </div>

      {/* TABLA */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha y hora</th>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Módulo</th>
                <th className="px-4 py-3 text-left font-semibold">Acción</th>
                <th className="px-4 py-3 text-left font-semibold">Descripción</th>
                <th className="px-4 py-3 text-right font-semibold">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Cargando...</td></tr>
              ) : error ? (
                <tr><td colSpan={6} className="text-center py-10 text-rose-500">{error}</td></tr>
              ) : data.length > 0 ? (
                data.map((b) => {
                  const a = ACCIONES.find((x) => x.value === b.accion);
                  const hayCambios = b.cambios && Object.keys(b.cambios).length > 0;
                  return (
                    <tr key={b.id_bitacora} className="border-t border-slate-100 hover:bg-slate-50 transition align-top">
                      <td className="px-4 py-3 text-slate-800 font-medium whitespace-nowrap">
                        <span className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                            <FaHistory className="text-xs" />
                          </span>
                          {formatoFechaHora(b.fecha)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{b.usuario || "—"}</td>
                      <td className="px-4 py-3 text-slate-700">{nombreModulo(b.modulo)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${a?.estilo || "bg-slate-100 text-slate-700"}`}>
                          {a?.label || b.accion}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700 max-w-[360px]">
                        <span className="line-clamp-2" title={b.resumen || ""}>{b.resumen || "—"}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {hayCambios ? (
                          <button
                            onClick={() => setDetalle(b)}
                            title="Ver cambios"
                            className="w-8 h-8 inline-flex items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition"
                          >
                            <FaEye className="text-xs" />
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan={6} className="text-center py-10 text-slate-400">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINACIÓN */}
      <div className="flex justify-between items-center mt-4">
        <span className="text-sm text-slate-500">
          {total === 0 ? "Sin resultados" : `Mostrando ${inicioRango} a ${finRango} de ${total} resultados`}
        </span>
        <div className="flex items-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronLeft className="text-xs" />
          </button>
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-indigo-600 text-white text-sm font-semibold">{page}</span>
          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage(page + 1)}
            className="w-8 h-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-500 disabled:opacity-40 hover:bg-slate-50 transition"
          >
            <FaChevronRight className="text-xs" />
          </button>
        </div>
      </div>

      {/* DETALLE DE CAMBIOS */}
      <Modal open={!!detalle} onClose={() => setDetalle(null)} title="Detalle del cambio" maxWidth="max-w-2xl">
        {detalle && (
          <div>
            <p className="text-sm text-slate-700 mb-1">{detalle.resumen}</p>
            <p className="text-xs text-slate-400 mb-4">
              {formatoFechaHora(detalle.fecha)} · {detalle.usuario || "Usuario desconocido"}
              {detalle.ip ? ` · IP ${detalle.ip}` : ""}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr>
                    <th className="py-2 pr-3 text-left font-semibold">Campo</th>
                    <th className="py-2 pr-3 text-left font-semibold">Antes</th>
                    <th className="py-2 text-left font-semibold">Después</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(detalle.cambios).map(([campo, c]) => (
                    <tr key={campo} className="border-t border-slate-100 align-top">
                      <td className="py-2 pr-3 text-slate-600 whitespace-nowrap">{nombreCampo(campo)}</td>
                      <td className="py-2 pr-3 text-rose-700 break-words">{formatoValor(c.antes)}</td>
                      <td className="py-2 text-emerald-700 break-words">{formatoValor(c.despues)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
